import React, { useState, useMemo } from "react";
import { Grid, Paper, Typography, Box, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import {
  useGetCampaignsAnalyticsQuery,
  useGetUsersCampaignsAnalyticsQuery,
} from "services/analytics";
import AnalyticsUsersTable from "../AnalyticsUsersTable";

const STATUS_META = {
  totalCampaigns: {
    label: "Total campaigns",
    icon: CampaignIcon,
    color: "#1976d2",
  },
  active: {
    label: "Active campaigns",
    icon: CheckCircleIcon,
    color: "#2e7d32",
  },
  paused: {
    label: "Paused campaigns",
    icon: PauseCircleIcon,
    color: "#ed6c02",
  },
  draft: { label: "Draft campaigns", icon: ScheduleIcon, color: "#0288d1" },
  completed: {
    label: "Completed campaigns",
    icon: CheckCircleIcon,
    color: "#388e3c",
  },
  error: { label: "Error campaigns", icon: ErrorOutlineIcon, color: "#d32f2f" },
  exhausted: {
    label: "Exhausted campaigns",
    icon: ReportProblemIcon,
    color: "#6d6d6d",
  },
  connectedEmailAccounts: {
    label: "Connected Email Accounts",
    icon: EmailIcon,
    color: "#6a1b9a",
  },
  connectedLinkedinAccounts: {
    label: "Connected LinkedIn Accounts",
    icon: LinkedInIcon,
    color: "#0A66C2",
  },
  totalEmailsSent: {
    label: "Total Emails Sent",
    icon: SendIcon,
    color: "#1565c0",
  },
  totalDfyInfraSetups: {
    label: "Total DFY",
    icon: AutoAwesomeIcon,
    color: "#61356dff",
  },
};

const CampaignAnalytics = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);

  const queryParams = useMemo(
    () => ({
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    }),
    [startDate, endDate],
  );

  const { data, isLoading, isError } = useGetCampaignsAnalyticsQuery();

  const {
    data: usersAnalytics = [],
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useGetUsersCampaignsAnalyticsQuery({
    ...queryParams,
    page,
    limit: 10,
  });

  const { metrics = {} } = data || {};
  // const { metrics = {}, chart = {} } = data || {};

  const buildCampaignAnalyticsCards = (summaryData = {}) =>
    Object.entries(summaryData).map(([key, value]) => {
      const meta = STATUS_META[key] || {
        label: key.replace(/([A-Z])/g, " $1"),
        icon: CampaignIcon,
        color: "#9e9e9e",
      };
      return {
        key,
        label: meta.label,
        value: value ?? 0,
        icon: meta.icon,
        color: meta.color,
      };
    });

  const analyticsCards = buildCampaignAnalyticsCards(metrics);

  if (isLoading) return <Typography>Loading analytics...</Typography>;
  if (isError)
    return <Typography color="error">Failed to load analytics</Typography>;

  return (
    <Grid container spacing={3}>
      {/* Analytics Cards */}
      {analyticsCards.map((item) => {
        const Icon = item.icon;
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderLeft: `5px solid ${item.color}`,
              }}
            >
              <Box sx={{ color: item.color }}>
                <Icon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h6">{item.value}</Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}

      {/* Date Filters (for users table) */}
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
            sx={{ ml: 2 }}
          />
        </LocalizationProvider>
      </Grid>

      {/* Users Analytics Table */}
      <Grid item xs={12}>
        <AnalyticsUsersTable
          columns={[
            {
              key: "name",
              label: "User",
              render: (row) => `${row.name.first} ${row.name.last}`,
            },
            { key: "email", label: "Email" },
            {
              key: "totalCampaigns",
              label: "Total",
              align: "center",
              render: (row) => row.campaigns.totalCampaigns,
            },
            {
              key: "active",
              label: "Active",
              align: "center",
              render: (row) => row.campaigns.active,
            },
            {
              key: "paused",
              label: "Paused",
              align: "center",
              render: (row) => row.campaigns.paused,
            },
            {
              key: "draft",
              label: "Draft",
              align: "center",
              render: (row) => row.campaigns.draft,
            },
            {
              key: "completed",
              label: "Completed",
              align: "center",
              render: (row) => row.campaigns.completed,
            },
            {
              key: "error",
              label: "Error",
              align: "center",
              render: (row) => row.campaigns.error,
            },
            {
              key: "exhausted",
              label: "Exhausted",
              align: "center",
              render: (row) => row.campaigns.exhausted,
            },
            {
              key: "connectedEmailAccounts",
              label: "Email Accounts",
              align: "center",
              render: (row) => row.connectedEmailAccounts ?? 0,
            },
            {
              key: "connectedLinkedinAccounts",
              label: "LinkedIn Accounts",
              align: "center",
              render: (row) => row.connectedLinkedinAccounts ?? 0,
            },
            {
              key: "totalEmailsSent",
              label: "Emails Sent",
              align: "center",
              render: (row) => row.totalEmailsSent ?? 0,
            },
            {
              key: "totalDfy",
              label: "Total DFY",
              align: "center",
              render: (row) => row.totalDfy ?? 0,
            },
          ]}
          rows={usersAnalytics?.data || []}
          loading={isUsersLoading}
          error={isUsersError}
          pagination={usersAnalytics?.pagination}
          onPageChange={setPage}
        />
      </Grid>
    </Grid>
  );
};

export default CampaignAnalytics;
